import { Component } from '@theme/component';

/**
 * A toggle button that shows/hides the inline vertical facets sidebar on the
 * collection page. Hiding the sidebar (via the `hidden` class) lets the product
 * grid reflow to full width — the reflow itself is handled in CSS through
 * `.collection-wrapper:has(.facets-block-wrapper--vertical:not(.hidden))`.
 *
 * Desktop only: on mobile the filters open in a drawer via a separate button.
 *
 * @extends {Component}
 */
class CollectionFiltersToggle extends Component {
  #storageKey = '';

  connectedCallback() {
    super.connectedCallback();

    this.#storageKey = `collection-filters-open-${this.getAttribute('section-id')}`;

    // Restore the previously chosen state (desktop only).
    const wasOpen = sessionStorage.getItem(this.#storageKey) === 'true';
    if (wasOpen && window.matchMedia('(min-width: 750px)').matches) {
      this.#setOpen(true, false);
    }
  }

  /**
   * The inline vertical facets pane this button controls.
   * @returns {HTMLElement | null}
   */
  get pane() {
    const sectionId = this.getAttribute('section-id');
    return document.querySelector(
      `.facets-block-wrapper--vertical[data-section-id="${sectionId}"]`
    );
  }

  /** Toggles the sidebar open/closed. */
  toggle() {
    const isOpen = this.pane != null && !this.pane.classList.contains('hidden');
    this.#setOpen(!isOpen, true);
  }

  /**
   * @param {boolean} open
   * @param {boolean} persist
   */
  #setOpen(open, persist) {
    const { pane } = this;
    if (!pane) return;

    pane.classList.toggle('hidden', !open);
    this.classList.toggle('collection-filters-toggle--active', open);

    const button = this.querySelector('button');
    button?.setAttribute('aria-expanded', String(open));
    this.setAttribute('aria-expanded', String(open));

    if (persist) sessionStorage.setItem(this.#storageKey, String(open));
  }
}

if (!customElements.get('collection-filters-toggle')) {
  customElements.define('collection-filters-toggle', CollectionFiltersToggle);
}
